import { useMemo } from 'react'

import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { RewardFareClass, type FareClassWithReward } from 'constants/flight'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'

interface Params {
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  allowedFareClasses: ReadonlyArray<FareClassWithReward>
}

export interface CheapestAvailableReward {
  fareClass: FareClassWithReward
  pointsPerPerson: number
  cashPerPerson: number
}

interface Result {
  cheapest: CheapestAvailableReward | null
  fetching: boolean
}

/**
 * Returns the cheapest affordable reward fare class that has live inventory
 * across the allowed reward fare classes for this charter offer. Returns
 * `cheapest: null` when no available reward fits within the member's points
 * balance (total across all seat-eligible passengers).
 *
 * Fans out a `useCharterFlightPrice` call per reward cabin — same pattern the
 * fare-class modal uses; React Query dedupes against the cards' downstream
 * fetches.
 */
export default function useCheapestAvailableReward({
  checkInDate,
  checkOutDate,
  occupancies,
  allowedFareClasses,
}: Params): Result {
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const memberPoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const hasDates = !!(checkInDate && checkOutDate)

  const allows = (rewardClass: FareClassWithReward) =>
    hasDates && hasLoyaltyAccount && allowedFareClasses.includes(rewardClass)

  const economy = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: RewardFareClass.ECONOMY_REWARDS,
    disabled: !allows(RewardFareClass.ECONOMY_REWARDS),
  })
  const premium = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: RewardFareClass.PREMIUM_REWARDS,
    disabled: !allows(RewardFareClass.PREMIUM_REWARDS),
  })
  const luxRow = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: RewardFareClass.LUXROW_REWARDS,
    disabled: !allows(RewardFareClass.LUXROW_REWARDS),
  })
  const business = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: RewardFareClass.BUSINESS_REWARDS,
    disabled: !allows(RewardFareClass.BUSINESS_REWARDS),
  })

  const cheapest = useMemo<CheapestAvailableReward | null>(() => {
    const candidates: Array<CheapestAvailableReward> = []

    const consider = (
      fareClass: FareClassWithReward,
      available: boolean,
      pointsPerPerson: number | undefined,
      pointsTotal: number | undefined,
      cashPerPerson: number | undefined,
    ) => {
      if (!available || pointsPerPerson == null || pointsTotal == null) return
      if (pointsTotal > memberPoints) return
      candidates.push({
        fareClass,
        pointsPerPerson,
        cashPerPerson: cashPerPerson ?? 0,
      })
    }

    consider(RewardFareClass.ECONOMY_REWARDS, economy.rewardAvailable, economy.rewardSeatPointsPerPerson, economy.rewardSeatPointsTotal, economy.rewardSeatCashCoPayPerPerson)
    consider(RewardFareClass.PREMIUM_REWARDS, premium.rewardAvailable, premium.rewardSeatPointsPerPerson, premium.rewardSeatPointsTotal, premium.rewardSeatCashCoPayPerPerson)
    consider(RewardFareClass.LUXROW_REWARDS, luxRow.rewardAvailable, luxRow.rewardSeatPointsPerPerson, luxRow.rewardSeatPointsTotal, luxRow.rewardSeatCashCoPayPerPerson)
    consider(RewardFareClass.BUSINESS_REWARDS, business.rewardAvailable, business.rewardSeatPointsPerPerson, business.rewardSeatPointsTotal, business.rewardSeatCashCoPayPerPerson)

    candidates.sort((a, b) => a.pointsPerPerson - b.pointsPerPerson)

    return candidates[0] ?? null
  }, [
    economy.rewardAvailable, economy.rewardSeatPointsPerPerson, economy.rewardSeatPointsTotal, economy.rewardSeatCashCoPayPerPerson,
    premium.rewardAvailable, premium.rewardSeatPointsPerPerson, premium.rewardSeatPointsTotal, premium.rewardSeatCashCoPayPerPerson,
    luxRow.rewardAvailable, luxRow.rewardSeatPointsPerPerson, luxRow.rewardSeatPointsTotal, luxRow.rewardSeatCashCoPayPerPerson,
    business.rewardAvailable, business.rewardSeatPointsPerPerson, business.rewardSeatPointsTotal, business.rewardSeatCashCoPayPerPerson,
    memberPoints,
  ])

  return {
    cheapest,
    fetching: economy.fetching || premium.fetching || luxRow.fetching || business.fetching,
  }
}
